import type { ServiceItem } from '@/cms/fields/types/checkout-page'
import { Banner, type BannerColors } from '@sunwebgroup/uds-ui/banner'
import { SwitchField } from '@sunwebgroup/uds-ui/fields/switch-field'
import { RadioButton } from '@sunwebgroup/uds-ui/radio-button'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { isEmpty, values } from 'ramda'
import * as React from 'react'
import { useSessionStorage } from 'react-use'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { SelectableListItem, SelectableRadioButtonGroup } from '@/scopes/checkout/components/SelectableListItem'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import { updatePerBookingService, updatePerParticipantService, updatePerRoomService } from '@/scopes/checkout/domain/actions'
import { createAncillariesClickedEvent, createGenericServiceEvent } from '@/scopes/checkout/domain/analytics'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useCheckoutSid, useGenericService } from '@/scopes/checkout/domain/hooks'
import {
  bannerColorMap,
  type CheckoutServiceBanner,
  type CheckoutServiceOption,
  ServiceType,
} from '@/scopes/checkout/domain/types'
import { ServiceRenderTypeEnum } from '@/scopes/checkout/infrastructure/api/types'
import { BannerType } from '@/scopes/common/domain/types/banner'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { AGE_CATEGORY_TRANSLATION_KEY, formatPriceRowText, getPriceForParticipant, getPricesPerAgeCategory } from '../priceHelpers'
import serviceStyles from '../ServiceComponents.module.css'
import styles from './GenericService.module.css'

export function GenericService({ item }: { item: ServiceItem }) {
  const { aName, renderType, componentTitle, componentDescription, popupButtonAriaLabel, popupTitle, popupDescription, popupReadMoreLabel, serviceConfigurations } = item
  const sid = useCheckoutSid()
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { service, setServiceValue } = useGenericService(ServiceType.GenericServicePerBooking, aName ?? '')
  const { service: servicePerParticipant } = useGenericService(ServiceType.GenericServicePerParticipant, aName ?? '')
  const { service: servicePerRoom } = useGenericService(ServiceType.GenericServicePerRoom, aName ?? '')

  const { showToggleForAllParticipants, toggleForAllParticipantsCheckedByDefault, toggleForAllParticipantsLabel } = serviceConfigurations || {}

  const [selectPerParticipantChecked, setSelectPerParticipantChecked] = useSessionStorage(
    `${sid}-generic-service-${aName?.toLowerCase()}-is-per-participant`,
    toggleForAllParticipantsCheckedByDefault,
  )

  const handleSwitchPerParticipantChange = React.useCallback(
    () => {
      setSelectPerParticipantChecked(!selectPerParticipantChecked)
      sendEvent(
        createGenericServiceEvent({
          description: 'Choose per participant',
          element: 'toggle',
          type: `select ${aName}`,
          result: !selectPerParticipantChecked ? 'element selected' : 'element unselected',
        }),
      )

      if (!selectPerParticipantChecked || !service)
        return

      const participants = Object.entries(service?.participants || {}).sort(
        ([keyA], [keyB]) => keyA.localeCompare(keyB),
      )

      const selectedValue = participants.find(([, value]) => value?.selectedValue)?.[1]?.selectedValue

      if (!selectedValue)
        return

      setServiceValue(updatePerBookingService({ value: selectedValue })(service))
    },
    [aName, sendEvent, service, setServiceValue, setSelectPerParticipantChecked, selectPerParticipantChecked],
  )

  React.useEffect(() => {
    if (!servicePerParticipant)
      return
    if (servicePerParticipant.participants && Object.keys(servicePerParticipant.participants).length === 1) {
      setSelectPerParticipantChecked(false)
    }
  })

  useSendAncillaryAvailable(service, `select ${aName}`)

  if (!service && !servicePerParticipant && !servicePerRoom)
    return null

  return (
    <CheckoutSection
      title={componentTitle ?? aName ?? ''}
      description={componentDescription}
      glyph={getIconForService(aName ?? '')}
      moreInformationTitle={popupTitle}
      moreInformationDescription={popupDescription}
      moreInformationButtonAriaLabel={popupButtonAriaLabel}
      moreInformationReadMoreLabel={popupReadMoreLabel}
    >
      {showToggleForAllParticipants && servicePerParticipant?.participants && (
        <div className={cn(styles.flexContent, styles.flexContentGap12)}>
          {Object.keys(servicePerParticipant.participants).length > 1 && (
            <SwitchField
              checked={selectPerParticipantChecked}
              label={toggleForAllParticipantsLabel ?? intl.formatMessage({ id: 'checkout_steps_ancillaries_generic_service_applies_to_all_participants' })}
              onCheckedChange={handleSwitchPerParticipantChange}
              variant="transparent"
              id={`generic-service-${aName?.replace(/\*/g, '').toLowerCase()}-is-per-participant-switch`}
            />
          )}
          {selectPerParticipantChecked ? <GenericServicePerParticipant aName={aName} /> : <GenericServicePerBooking aName={aName} />}
        </div>
      )}

      {!showToggleForAllParticipants && (
        <div className={cn(styles.flexContent, styles.flexContentGap12)}>
          { renderType === ServiceRenderTypeEnum.PerBooking && (
            <GenericServicePerBooking aName={aName} />
          )}

          { renderType === ServiceRenderTypeEnum.PerParticipant && (
            <GenericServicePerParticipant aName={aName} />
          )}

          { renderType === ServiceRenderTypeEnum.PerRoom && (
            <GenericServicePerRoom aName={aName} />
          )}

        </div>
      )}
    </CheckoutSection>
  )
}

export function GenericServicePerBooking({ aName }: { aName?: string }) {
  const intl = useIntl()
  const { service, setServiceValue } = useGenericService(ServiceType.GenericServicePerBooking, aName ?? '')
  const { sendEvent } = useAnalytics()

  const handleValueChange = React.useCallback(
    (value: string) => {
      if (!service)
        return
      setServiceValue(updatePerBookingService({ value })(service))
      const optionClicked = service.options.find(option => option.value === value)
      sendEvent(
        createAncillariesClickedEvent({
          result: optionClicked?.price || 0,
          type: optionClicked?.label || '',
          description: `select ${aName}`,
          element: 'radiobutton',
        }),
      )
    },
    [aName, sendEvent, setServiceValue, service],
  )

  if (!service)
    return null

  const { options, selectedValue, banners, participants } = service

  return (
    <div className={cn(styles.flexContent, styles.flexContentGap12)}>
      <SelectableRadioButtonGroup value={selectedValue ?? undefined} onValueChange={handleValueChange}>
        <div className={styles.optionItemsContainer}>
          {options.map((option) => {
            const { label, badge, price, percentage, value } = option
            const priceRows = getPricesPerAgeCategory(option, participants)

            return (
              <SelectableListItem value={value} className={styles.itemOptionWrapper} key={value}>
                <div className={styles.itemOption}>
                  <div className={styles.itemOptionControl}>
                    <RadioButton value={value} data-qa="generic-service-per-booking-option" />
                  </div>
                  <div className={styles.itemOptionContent}>
                    {badge && <Tag variant="success-tinted" size="M" className={cn(styles.itemOptionTag)}>{badge}</Tag>}
                    <Typography className={styles.optionValue}>{label}</Typography>
                  </div>
                  <div className={styles.itemOptionTrailing}>
                    {priceRows.length > 0
                      ? (
                          priceRows.map(({ ageCategory, prices }) => (
                            <Typography className={styles.optionPrice} key={ageCategory}>
                              {intl.formatMessageIfExists({ id: AGE_CATEGORY_TRANSLATION_KEY[ageCategory] })}
                              {' '}
                              {formatPriceRowText(
                                prices,
                                rowPrice => intl.formatMessage({ id: 'checkout_steps_price_per_person' }, { price: intl.formatCurrency(rowPrice, { showSign: rowPrice < 0 }) }),
                                (minimumPrice, maximumPrice) => intl.formatMessage(
                                  { id: 'checkout_steps_price_per_person_range' },
                                  {
                                    minimumPrice: intl.formatCurrency(minimumPrice, { showSign: minimumPrice < 0 }),
                                    maximumPrice: intl.formatCurrency(maximumPrice, { showSign: maximumPrice < 0 }),
                                  },
                                ),
                              )}
                            </Typography>
                          ))
                        )
                      : (
                          <Typography className={styles.optionPrice}>
                            {percentage || intl.formatCurrency(price, { showSign: price < 0 })}
                          </Typography>
                        )}

                  </div>
                </div>
              </SelectableListItem>
            )
          })}
        </div>
      </SelectableRadioButtonGroup>
      {banners.length > 0 && (
        <Banners banners={banners} />
      )}
    </div>
  )
}

function GenericServicePerRoom({ aName }: { aName?: string }) {
  const { service, setServiceValue } = useGenericService(ServiceType.GenericServicePerRoom, aName ?? '')
  const { sendEvent } = useAnalytics()

  const handleValueChange = React.useCallback(
    (roomId: string, value: string) => {
      if (!service)
        return
      setServiceValue(updatePerRoomService({ roomId, value })(service))

      const optionClicked = service.options[value]
      sendEvent(
        createAncillariesClickedEvent({
          result: optionClicked?.price || 0,
          type: optionClicked?.label ? `${optionClicked?.label} for room ${roomId}` : '',
          description: `select ${aName}`,
          element: 'radiobutton',
        }),
      )
    },
    [aName, sendEvent, setServiceValue, service],
  )

  if (!service)
    return null

  const { rooms, banners } = service

  return (
    <div className={cn(styles.flexContent, styles.flexContentGap12)}>
      {Object.values(rooms ?? {}).map(room => (
        <RoomRow
          key={room.id}
          {...room}
          aName={aName}
          onValueChange={handleValueChange}
        />
      ))}

      {banners.length > 0 && (
        <Banners banners={banners} />
      )}
    </div>
  )
}

function RoomRow({
  id,
  availableOptions,
  selectedValue,
  name,
  aName,
  onValueChange,
}: {
  id: string
  availableOptions?: string[]
  selectedValue?: string
  aName?: string
  name: string
  onValueChange: (roomId: string, value: string) => void
}) {
  const intl = useIntl()
  const { service } = useGenericService(ServiceType.GenericServicePerRoom, aName ?? '')

  const handleValueChange = React.useCallback(
    (value: string) => {
      onValueChange(id, value)
    },
    [id, onValueChange],
  )

  if (!service)
    return null

  const { options, rooms } = service

  return (
    <div className={cn(styles.flexContent, styles.flexContentGap16)} key={id}>
      {Object.values(rooms ?? {}).length > 1 && (
        <Typography as="p" className={serviceStyles.roomName}>
          {name}
        </Typography>
      )}
      <SelectableRadioButtonGroup value={selectedValue ?? undefined} onValueChange={handleValueChange}>
        <div className={styles.optionItemsContainer}>
          {(availableOptions ?? []).map((optionId) => {
            const { value, label, badge, price } = options[optionId]
            return (
              <SelectableListItem value={value} className={styles.itemOptionWrapper} key={value}>
                <div className={styles.itemOption}>
                  <div className={styles.itemOptionControl}>
                    <RadioButton value={value} data-qa="generic-service-per-room-option" />
                  </div>
                  <div className={styles.itemOptionContent}>
                    {badge && <Tag variant="success-tinted" size="M" className={cn(styles.itemOptionTag)}>{badge}</Tag>}
                    <Typography className={styles.optionValue}>{label}</Typography>
                  </div>
                  <div className={styles.itemOptionTrailing}>
                    <Typography className={styles.optionPrice}>
                      {intl.formatCurrency(price, { showSign: price < 0 })}
                    </Typography>
                  </div>
                </div>
              </SelectableListItem>
            )
          },
          )}
        </div>
      </SelectableRadioButtonGroup>
    </div>
  )
}

export function GenericServicePerParticipant({ aName }: { aName?: string }) {
  const { service, setServiceValue } = useGenericService(ServiceType.GenericServicePerParticipant, aName ?? '')

  const { sendEvent } = useAnalytics()

  const handleValueChange = React.useCallback(
    (participantId: string, value: string) => {
      if (!service)
        return
      setServiceValue(updatePerParticipantService({ participantId, value })(service))
      const optionClicked = service.options[value]
      sendEvent(
        createAncillariesClickedEvent({
          result: optionClicked?.price || 0,
          type: optionClicked?.label ? `${optionClicked?.label} for participant ${participantId}` : '',
          description: `select ${aName}`,
          element: 'radiobutton',
        }),
      )
    },
    [aName, sendEvent, setServiceValue, service],
  )

  const roomsToDisplay = React.useMemo(() => {
    if (!service)
      return null

    const { rooms, participants } = service

    const roomsWithAvailableOptionsThatAppliesToParticipants = Object.values(participants).filter(({ availableOptions }) => availableOptions.length > 0).map(({ roomId }) => roomId)
    return rooms.filter(room => roomsWithAvailableOptionsThatAppliesToParticipants.includes(room.id))
  }, [service])

  if (!service || !roomsToDisplay)
    return null

  const { options, participants, banners } = service

  return (
    <div className={cn(styles.flexContent, styles.flexContentGap12)}>
      {roomsToDisplay.map(({ id: roomId, name: roomName, participantIds }) => (
        <div className={cn(styles.flexContent, styles.flexContentGap16)} key={roomId}>
          {Object.values(roomsToDisplay ?? {}).length > 1 && (
            <Typography as="p" className={serviceStyles.roomName}>
              {roomName}
            </Typography>
          )}
          {participantIds.map((participantId) => {
            const participant = participants?.[participantId]
            if (!participant || participant.availableOptions.length === 0) {
              return null
            }

            return (
              <ParticipantRow
                {...participant}
                key={participantId}
                options={options}
                onValueChange={handleValueChange}
                showName={values(participants).length > 1}
              />
            )
          })}
        </div>
      ))}
      {isEmpty(roomsToDisplay)
      && Object.values(participants ?? {}).map(participant => (
        <ParticipantRow {...participant} key={participant.id} options={options} onValueChange={handleValueChange} showName={values(participants).length > 1} />
      ))}

      {banners.length > 0 && (
        <Banners banners={banners} />
      )}
    </div>
  )
}

function ParticipantRow({
  id,
  options,
  availableOptions,
  selectedValue,
  name,
  onValueChange,
  showName = true,
}: {
  id: string
  options: Record<string, CheckoutServiceOption>
  availableOptions: string[]
  name: string
  selectedValue: string | null
  onValueChange: (participantId: string, value: string) => void
  showName?: boolean
}) {
  const intl = useIntl()

  const handleValueChange = React.useCallback(
    (value: string) => {
      onValueChange(id, value)
    },
    [id, onValueChange],
  )

  return (
    <div className={cn(styles.flexContent, styles.flexContentGap8)}>
      {showName && (
        <Typography className={serviceStyles.participantName}>
          {name}
        </Typography>
      )}

      <SelectableRadioButtonGroup value={selectedValue ?? undefined} onValueChange={handleValueChange}>
        <div className={styles.optionItemsContainer}>
          {(availableOptions ?? []).map((optionId) => {
            const option = options[optionId]
            const { value, label, badge, price, rawPrice } = option
            // rawPrice is the individual flat price; price may be an aggregated total for other render types.
            const participantPrice = getPriceForParticipant({ priceAssignments: option.priceAssignments, price: rawPrice ?? price }, id)
            return (
              <SelectableListItem value={value} className={styles.itemOptionWrapper} key={value}>
                <div className={styles.itemOption}>
                  <div className={styles.itemOptionControl}>
                    <RadioButton value={value} data-qa="generic-service-per-participant-option" />
                  </div>
                  <div className={styles.itemOptionContent}>
                    {badge && <Tag variant="success-tinted" size="M" className={cn(styles.itemOptionTag)}>{badge}</Tag>}
                    <Typography className={styles.optionValue}>{label}</Typography>
                  </div>
                  <div className={styles.itemOptionTrailing}>
                    <Typography className={styles.optionPrice}>
                      {intl.formatCurrency(participantPrice, { showSign: participantPrice < 0 })}
                    </Typography>
                  </div>
                </div>
              </SelectableListItem>
            )
          },
          )}
        </div>
      </SelectableRadioButtonGroup>
    </div>
  )
}

function Banners({ banners }: { banners: CheckoutServiceBanner[] }) {
  return (
    <>
      {banners.map(({ title, text, type = 'info' }, index) => {
        return (
          <Banner
            key={`banner-${index}`}
            color={bannerColorMap[type as BannerType] as BannerColors}
            size="M"
          >
            <Banner.TextsWrapper>
              {title && <Banner.Text>{title}</Banner.Text>}
              {text && <Banner.SupportingText innerHTML={text} />}
            </Banner.TextsWrapper>
          </Banner>
        )
      },
      )}
    </>
  )
}
